import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api, resendVerificationApi } from '../api';
import { useTheme } from '../contexts/ThemeContext';
import './Auth.css';

function AuthVisual() {
  return (
    <div className="auth-visual">
      <img
        src="https://images.unsplash.com/photo-1736010756213-a581798d2c8d?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
        alt=""
        className="auth-bg-image"
      />
      <div className="auth-visual-overlay" />
    </div>
  );
}

export default function Auth() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [login, setLogin] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // When set, render the "check your email" panel instead of the form.
  const [pendingVerification, setPendingVerification] = useState(null);
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent

  const { signIn, signUp } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const pendingPlan = params.get('plan');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isSignUp) {
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
    }

    setLoading(true);
    try {
      if (isSignUp) {
        const result = await signUp({ email, username, password });
        if (result?.verificationRequired) {
          setPendingVerification({ email: result.email || email });
          return;
        }
      } else {
        await signIn({ login, password });
      }
      if (pendingPlan && pendingPlan !== 'free' && pendingPlan !== 'enterprise') {
        try {
          const { url } = await api.post('/api/billing/checkout', { plan_id: pendingPlan });
          window.location.href = url;
          return;
        } catch (_e) {}
      }
      navigate('/portal');
    } catch (err) {
      // Sign-in with an unverified account returns 403 with verification_required.
      if (err?.status === 403 && err?.data?.verification_required) {
        setPendingVerification({ email: err.data.email || login });
        return;
      }
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (signUp) => {
    setIsSignUp(signUp);
    setError('');
    setPendingVerification(null);
    setResendState('idle');
  };

  const handleResend = async () => {
    if (!pendingVerification?.email) return;
    setResendState('sending');
    try {
      await resendVerificationApi(pendingVerification.email);
      setResendState('sent');
    } catch (_e) {
      setResendState('idle');
    }
  };

  if (pendingVerification) {
    return (
      <div className="auth-page">
        <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? '○' : '●'}
        </button>
        <div className="auth-left">
          <AuthVisual />
          <div className="auth-left-content">
            <span className="auth-left-wordmark">AEGENT</span>
            <h2 className="auth-left-headline">Build systems<br />that think.</h2>
          </div>
        </div>
        <div className="auth-right">
          <div className="auth-form-container">
            <div className="auth-form-header">
              <h1 className="t-caption">Verify your email</h1>
            </div>
            <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 24, lineHeight: 1.5 }}>
              We sent a verification link to <strong style={{ color: 'var(--fg)' }}>{pendingVerification.email}</strong>.
              Click the link to activate your account, then sign in.
            </p>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleResend}
              disabled={resendState !== 'idle'}
              style={{ width: '100%' }}
            >
              {resendState === 'sending' ? 'Sending…'
                : resendState === 'sent' ? 'Sent — check your inbox'
                : 'Resend verification email'}
            </button>
            <div className="auth-switch-row">
              <span>Already verified?</span>
              <button className="auth-switch-btn" onClick={() => switchMode(false)}>
                Sign In
              </button>
            </div>
          </div>
          <div className="auth-footer-brand">
            <span>AEGENT by akakAI</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
        {theme === 'dark' ? '○' : '●'}
      </button>

      <div className="auth-left">
        <AuthVisual />
        <div className="auth-left-content">
          <span className="auth-left-wordmark">AEGENT</span>
          <h2 className="auth-left-headline">Build systems<br />that think.</h2>
        </div>
      </div>

      <div className="auth-right">
        <div className="auth-form-container">
          <div className="auth-form-header">
            <h1 className="t-caption">{isSignUp ? 'Create Account' : 'Sign In'}</h1>
          </div>

          {error && <div className="auth-error">{error}</div>}

          <form onSubmit={handleSubmit} className="auth-form">
            {isSignUp ? (
              <>
                <div className="form-field">
                  <label className="label">Email</label>
                  <input
                    type="email"
                    className="input"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="form-field">
                  <label className="label">Username</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Choose a username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>
              </>
            ) : (
              <div className="form-field">
                <label className="label">Email or Username</label>
                <input
                  type="text"
                  className="input"
                  placeholder="you@example.com"
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  required
                />
              </div>
            )}

            <div className="form-field">
              <label className="label">Password</label>
              <input
                type="password"
                className="input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {isSignUp && (
              <div className="form-field">
                <label className="label">Confirm Password</label>
                <input
                  type="password"
                  className="input"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary auth-submit"
              disabled={loading}
            >
              {loading ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Sign In')}
            </button>
          </form>

          <div className="auth-switch-row">
            <span>{isSignUp ? 'Already have an account?' : "Don't have an account?"}</span>
            <button className="auth-switch-btn" onClick={() => switchMode(!isSignUp)}>
              {isSignUp ? 'Sign In' : 'Create Account'}
            </button>
          </div>
        </div>

        <div className="auth-footer-brand">
          <span>AEGENT by akakAI</span>
        </div>
      </div>
    </div>
  );
}
